import { createAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppErrorManager } from 'errorHelper';
import aiMusicGeneratorApi from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import { useMessageSnackbar, useSubscription } from 'services/hooks';
import { useAuthProtectedAction } from 'services/hooks/user';
import { AppDispatch } from 'services/store';
import { selectCurrentPrompt } from 'services/store/ai/selectors';
import { FormulaFields, PromptType } from 'services/store/ai/types';
import { selectAIGenerationLimit } from 'services/store/user/selectors';
import useAnalytics from 'services/hooks/analytics/useAnalytics';
import { events } from 'services/analytics';
import { formulaToRecord } from 'pages/AIMusicGenerator/helpers/formula';

const PAID_FORMULA_FIELDS = [FormulaFields.Duration];

export const triggerAIGenerationLimitReached = createAction(
  'aiGenerationLimitReached'
);

const useFormulaSubmit = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const { displayErrorSnackbar } = useMessageSnackbar();

  const [createBundle] = aiMusicGeneratorApi.useCreateBundleMutation();

  const prompt = useSelector(selectCurrentPrompt);

  const { isFreeAccount } = useSubscription();

  const aiGenerationLimit = useSelector(selectAIGenerationLimit);

  const { track } = useAnalytics();

  const submitHandler = useCallback(async () => {
    if (!prompt) {
      return;
    }

    const { primaryGenre, promptType } = prompt;

    if (!aiGenerationLimit?.remaining) {
      dispatch(triggerAIGenerationLimitReached());
      return;
    }

    const isAdvancedFormula = promptType === PromptType.AdvancedFormula;
    if (isAdvancedFormula && !primaryGenre) {
      return;
    }

    const omitFields = isFreeAccount ? PAID_FORMULA_FIELDS : [];

    const payload = {
      ...prompt,
      formula:
        prompt.formula?.filter(({ field }) => !omitFields?.includes(field)) ||
        [],
    };

    const formulaRecord = formulaToRecord(payload.formula);

    track(
      events.aiGenerateButtonAnalytics({
        primaryGenre,
        subGenre: formulaRecord.genre_blend,
        formulaValues: formulaRecord,
        promptText: payload.text,
        promptType,
      })
    );

    try {
      await createBundle(payload).unwrap();
    } catch (error) {
      displayErrorSnackbar(t('messages:error:unknown'));
      AppErrorManager.sendError(new Error('vega_bundle_generation_error'), {
        extra: {
          error,
          payload,
        },
      });
    }
  }, [
    dispatch,
    createBundle,
    prompt,
    aiGenerationLimit,
    isFreeAccount,
    displayErrorSnackbar,
    t,
    track,
  ]);

  const protectedSubmitHandler = useAuthProtectedAction(submitHandler);

  return protectedSubmitHandler;
};

export default useFormulaSubmit;
